<template>
  <div class="right-section">
    <div class="top">
      <EnhancedQSelect
        v-if="fromPage !== 'templates' && !editItem"
        id="templates"
        v-model="template"
        :loading="!templateOptions"
        :disable="fromPage === 'templates'"
        :readonly="fromPage === 'templates'"
        :options="templateOptions || []"
        label="Template"
      />

      <EnhancedQSelect v-model="payload.priority_id" :options="priorities" :filter="false" label="Priority">
        <template #option="{props}">
          <q-item v-bind="props.itemProps" v-on="props.itemEvents">
            <q-item-section avatar>
              <q-icon :color="props.opt.leftColor" :name="props.opt.icon" />
            </q-item-section>
            <q-item-section>
              <q-item-label v-html="props.opt.label" />
            </q-item-section>
          </q-item>
        </template>
      </EnhancedQSelect>

      <LmDatePicker
        ref="deadline-picker"
        v-model="payload.deadline"
        :options="isDeadlineSameOrAfterToday"
        label="Deadline"
      />

      <EnhancedQSelect
        v-if="fromPage !== 'templates'"
        id="relate-to"
        v-model="payload.entity"
        :disable="!!entity"
        :readonly="!!entity"
        :options="relateToOptions"
        label="Relate To"
      />

      <EnhancedQSelect
        v-if="fromPage !== 'templates'"
        v-model="$v.payload.entity_id.$model"
        :error="$v.payload.entity_id.$error"
        :options="entityOptions"
        :disable="!payload.entity || !!entity"
        :readonly="!payload.entity || !!entity"
        :filter="payload.entity && !entity"
        label="Entity"
        hide-bottom-space
        no-error-icon
      >
        <template v-if="$v.payload.entity_id.$error" #append>
          <q-icon class="fa fa-exclamation-circle" color="negative">
            <LmTooltip>{{ $v_displayMessage($v.payload.entity_id) }}</LmTooltip>
          </q-icon>
        </template>
      </EnhancedQSelect>

      <q-select
        v-model="payload.owner_ids"
        :options="owners"
        label="Assigned to"
        emit-value
        map-options
        multiple
        outlined
      />

      <q-select
        v-if="!!editItem"
        v-model="subscriberIds"
        :options="owners"
        label="Subscribers"
        emit-value
        map-options
        multiple
        outlined
        @input="subscriberIdsChanged = true"
      />

      <EnhancedQSelect
        v-model="boardId"
        :disable="!payload.in_kanban"
        :readonly="!payload.in_kanban"
        :options="boards"
        label="Board"
        @input="boardChanged"
      />

      <EnhancedQSelect
        v-model="$v.payload.state_id.$model"
        :error="$v.payload.state_id.$error"
        :disable="!payload.in_kanban || !boardId"
        :readonly="!payload.in_kanban || !boardId"
        :options="states"
        label="State"
        hide-bottom-space
        no-error-icon
      >
        <template v-if="$v.payload.state_id.$error" #append>
          <q-icon class="fa fa-exclamation-circle" color="negative">
            <LmTooltip>{{ $v_displayMessage($v.payload.state_id) }}</LmTooltip>
          </q-icon>
        </template>
      </EnhancedQSelect>

      <q-checkbox v-model="payload.in_kanban" :disable="!payload.in_dashboard" label="In Kanban" color="tertiary" />

      <q-checkbox v-model="payload.in_dashboard" :disable="!payload.in_kanban" label="In Dashboard" color="tertiary" />
    </div>
    <div class="bottom">
      <q-btn v-if="editItem" color="white" class="text-red-11" style="width: auto" @click="$emit('delete')">
        Delete
      </q-btn>

      <q-btn
        v-if="!editItem && fromPage !== 'templates'"
        :disable="disableSaveAsTemplate"
        class="text-black"
        color="white"
        style="width: auto"
        @click="onSaveAsTemplate"
      >
        Save as template
      </q-btn>

      <q-btn color="tertiary" style="width: auto" @click="onSave">
        Save
      </q-btn>
    </div>
  </div>
</template>

<script>
import { validations } from './validations'
// Libraries
import { mapState, mapGetters, mapActions } from 'vuex'
import moment from 'moment'
import { cloneDeep, capitalize } from 'lodash'
// Components
import EnhancedQSelect from '@components/general/EnhancedQSelect'
import LmDatePicker from '@/reusable/LmDatePicker/LmDatePicker'

export default {
  components: {
    EnhancedQSelect,
    LmDatePicker
  },

  mixins: [validations],

  props: {
    fromPage: String,
    stateId: Number,
    editItem: Object,
    title: String,
    disableSaveAsTemplate: Boolean
  },

  data: () => ({
    boardId: null,
    template: null,
    payload: {
      priority_id: null,
      owner_ids: [],
      deadline: null,
      state_id: null,
      in_kanban: false,
      in_dashboard: false,
      entity: null,
      entity_id: null
    },
    subscriberIds: [],
    subscriberIdsChanged: false
  }),

  computed: {
    ...mapState('todo-modal', ['createData', 'entity', 'selectedTemplate']),
    ...mapGetters('todo-modal', ['templateOptions']),

    weAreInEntityPage() {
      return ['publisher', 'vendor', 'product', 'license-period', 'subscription'].includes(this.fromPage)
    },

    relateToOptions() {
      if (!this.createData) return []
      if (this.weAreInEntityPage) {
        return [
          {
            label:
              this.fromPage
                .replace('-', ' ')
                .slice(0, 1)
                .toUpperCase() + this.fromPage.replace('-', ' ').slice(1),
            value: this.fromPage
          }
        ]
      }

      return this.generateOptions({
        collection: Object.keys(this.createData.entities),
        labelKey: null,
        valueKey: null,
        sort: false,
        includeNone: true,
        customLabelDisplay: label => capitalize(label.replace('-', ' ')),
        sortOptions: { sortBy: null }
      })
    },

    entityOptions() {
      if (!this.payload.entity) return []
      if (this.fromPage === 'subscription') return [{ label: this.entity.product.name, value: this.entity.id }]
      if (this.weAreInEntityPage) return [{ label: this.entity.name, value: this.entity.id }]
      return this.generateOptions({
        collection: this.createData.entities[this.payload.entity]
      })
    },

    priorities() {
      if (!this.createData) return []
      const priorities = this.createData.priorities.map(p => {
        const map = {
          high: {
            label: 'High',
            icon: 'fas fa-exclamation-triangle',
            leftColor: 'red',
            value: p.id
          },
          medium: {
            label: 'Medium',
            icon: 'fas fa-exclamation-circle',
            leftColor: 'yellow',
            value: p.id
          },
          low: {
            label: 'Low',
            value: p.id,
            icon: null
          }
        }
        return map[p.name]
      })
      return [...priorities, { label: 'None', value: null }]
    },

    owners() {
      if (!this.createData) return []
      return this.generateOptions({
        collection: this.createData.owners,
        includeNone: false,
        sortBefore: true,
        sort: false,
        sortingOptions: {
          sortDataType: 'string',
          sortBy: 'name',
          sortOrder: 'asc'
        }
      })
    },

    boards() {
      if (!this.createData) return []
      return this.generateOptions({
        collection: this.createData.boards,
        sortBefore: true,
        sort: false,
        sortingOptions: {
          sortDataType: 'string',
          sortBy: 'name',
          sortOrder: 'asc'
        }
      })
    },

    states() {
      if (!this.createData || !this.boardId) return []
      const selectedBoard = this.createData.boards.find(b => b.id === this.boardId)
      return this.generateOptions({
        collection: selectedBoard.states,
        sortBefore: true,
        sort: false,
        sortingOptions: {
          sortDataType: 'string',
          sortBy: 'name',
          sortOrder: 'asc'
        }
      })
    },

    canCreateTodo() {
      return !(
        (
          (this.payload.entity && !this.payload.entity_id) || // can't save if we have relate to but not entity
          !this.title || // can't save if we don't have title
          (this.payload.in_kanban && !this.payload.state_id)
        ) // can't save if we check in kanban but don't select state
      )
    },

    defaultBoard() {
      if (!this.createData) return
      return this.createData.boards.find(b => b.system)
    },

    defaultState() {
      if (!this.createData) return
      return this.createData.states.find(
        s => s.board.id === this.defaultBoard.id && s.state_type.name === 'unscheduled'
      )
    }
  },

  watch: {
    stateId: {
      handler(val) {
        if (val) this.payload.in_kanban = true
        else this.payload.in_dashboard = true
      },
      immediate: true
    },

    'payload.in_kanban'(val) {
      if (!val) {
        this.boardId = null
        this.payload.state_id = null
      } else {
        let stateId = this.stateId
        if (this.editItem) stateId = this.editItem.state ? this.editItem.state.id : null
        const boardOfState = stateId => this.createData.boards.find(b => b.states.some(s => s.id === stateId))
        this.boardId = stateId ? boardOfState(stateId).id : this.defaultBoard.id
        this.payload.state_id = stateId || this.defaultState.id
      }
    },

    'payload.entity'(val) {
      if (!val) this.payload.entity_id = null
    },

    template(val) {
      this.getAndSetSelectedTemplate(val)
    },

    selectedTemplate(template) {
      this.updateFields(cloneDeep(template))
      this.setStateAndEntity()
    },

    createData(val) {
      if (val && !this.editItem) {
        if (this.fromPage === 'templates') this.payload.in_kanban = true
      }
    }
  },

  created() {
    this.setStateAndEntity()
  },

  methods: {
    ...mapActions('todo-modal', ['getAndSetSelectedTemplate']),

    boardChanged() {
      // clear state id when changing board
      this.payload.state_id = null
    },

    setStateAndEntity() {
      if (this.stateId) {
        this.boardId = Number(this.$route.params.id)
        this.payload.state_id = this.stateId
      }
      if (this.weAreInEntityPage) {
        this.payload.entity = this.fromPage
        this.payload.entity_id = this.entity.id
      }
    },

    updateFields(item) {
      // access from parent
      const deadline = item.deadline ? moment(item.deadline, 'YYYY-MM-DD').format(this.settings.date_format) : ''
      this.payload = {
        deadline,
        priority_id: item.priority ? item.priority.id : null,
        owner_ids: item.owners.map(o => o.id),
        state_id: item.state ? item.state.id : null,
        in_kanban: item.in_kanban,
        in_dashboard: item.in_dashboard,
        entity: item.entity ? item.entity.type : null,
        entity_id: item.entity ? item.entity.id : null
      }
      this.subscriberIds = [...item.subscriber_user_ids]
    },

    isDeadlineSameOrAfterToday(date) {
      return moment(date, 'YYYY/MM/DD').isSameOrAfter(moment().format('YYYY-MM-DD'))
    },

    getPayloadForSave() {
      const payload = this.payload
      payload.deadline = this.formatDateForPost(this.payload.deadline, this.settings.date_format)
      return payload
    },

    onSave() {
      this.$v.$touch()
      if (!this.canCreateTodo) return
      this.$emit('save', this.getPayloadForSave(), this.subscriberIdsChanged ? this.subscriberIds : null)
    },

    onSaveAsTemplate() {
      this.$emit('save-as-template', this.getPayloadForSave())
    }
  }
}
</script>

<style lang="stylus" scoped>
.right-section {
  display grid
  grid-template-rows 1fr auto
  align-items start
  position sticky
  top 15px

  .top, .bottom {
    display grid
    gap 10px

    > .q-field {
      background white
    }
  }
}

@media only screen and (min-height: 900px) {
  .right-section {
    height calc(100vh - 230px)
  }
}
</style>
